import { useCallback, useMemo, useState } from 'react'
import { Check, X, Inbox } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge, type StatusKind } from '@/components/StatusBadge'
import { RelativeTime } from '@/components/DataDisplay'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'
import { fetchAppQueue, moderateApp, APP_MOD_STR as S, type AppQueueRow } from './appModApi'

/**
 * A5.18 — Moderatsiya navbati (yangi Mini App'lar). GET /admin/mini-apps/queue
 * (lane-A greenfield → fail-soft bo'sh). Tasdiq/rad → POST /admin/mini-apps/:id/moderate.
 */
export function AppModerationQueue() {
  const { toast } = useToast()
  const [rejectTarget, setRejectTarget] = useState<AppQueueRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(S.moderated, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
        throw e
      }
    },
    [toast],
  )

  const statusKind = (s: string): StatusKind => (s === 'approved' ? 'approved' : s === 'rejected' ? 'rejected' : 'pending')
  const statusLabel = (s: string) => (s === 'approved' ? S.approved : s === 'rejected' ? S.rejected : S.pending)

  const columns = useMemo<AdminColumn<AppQueueRow>[]>(
    () => [
      {
        key: 'title',
        header: S.app,
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <div className="min-w-0">
            <span className="block truncate font-medium text-primary">{r.title}</span>
            <code className="text-xs text-accent">/{r.short_name}</code>
          </div>
        ),
      },
      {
        key: 'bot_username',
        header: S.bot,
        type: 'text',
        width: 160,
        render: (r) => (r.bot_username ? `@${r.bot_username}` : '—'),
      },
      {
        key: 'url',
        header: S.url,
        type: 'text',
        minWidth: 200,
        render: (r) => <span className="break-all font-mono text-xs text-secondary">{r.url}</span>,
      },
      {
        key: 'status',
        header: S.status,
        type: 'text',
        width: 140,
        render: (r) => <StatusBadge status={statusKind(r.status)} label={statusLabel(r.status)} />,
      },
      {
        key: 'submitted_at',
        header: S.submitted,
        type: 'date',
        width: 150,
        render: (r) => <RelativeTime value={r.submitted_at} className="text-secondary" />,
      },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<AppQueueRow>[]>(
    () => [
      {
        id: 'approve',
        label: S.approve,
        icon: <Check size={13} />,
        visible: (r) => r.status !== 'approved',
        onSelect: (r) => void run(() => moderateApp(r.id, 'approve')),
      },
      {
        id: 'reject',
        label: S.reject,
        icon: <X size={13} />,
        danger: true,
        visible: (r) => r.status === 'pending',
        onSelect: (r) => setRejectTarget(r),
      },
    ],
    [run],
  )

  const fetcher = useCallback(fetchAppQueue, [])

  return (
    <>
      <PageHeader title={S.queueTitle} screenId="A5.18" />
      <DataTable<AppQueueRow>
        key={reloadKey}
        screenId="A5.18"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Inbox size={28} strokeWidth={1.5} />, title: S.queueEmpty }}
      />

      {rejectTarget && (
        <DangerDialog
          open
          onClose={() => setRejectTarget(null)}
          screenId="A5.18"
          level={2}
          title={`${S.rejectTitle}`}
          description={S.rejectDesc}
          confirmLabel={S.reject}
          onConfirm={async ({ reason }) => {
            await run(() => moderateApp(rejectTarget.id, 'reject', reason))
            setRejectTarget(null)
          }}
        />
      )}
    </>
  )
}
